import { 
  NotificationPreference, 
  UpdateNotificationPreferenceRequest, 
  NotificationHistoryItem,
  NotificationType
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Utility function to safely check notification support
const isNotificationSupported = (): boolean => {
  return typeof window !== 'undefined' && 'Notification' in window;
};

// Utility function to safely get notification permission
const getNotificationPermission = (): NotificationPermission => {
  if (!isNotificationSupported()) {
    return 'denied';
  }
  return Notification.permission;
};

class NotificationApiService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Get token from localStorage for authenticated requests (matching authApi pattern)
    const token = localStorage.getItem('auth-store');
    let authToken = null;
    
    if (token) {
      try {
        const parsed = JSON.parse(token);
        authToken = parsed.state?.token;
      } catch (error) {
        console.error('Failed to parse auth token:', error);
      }
    }

    if (authToken) {
      defaultHeaders['Authorization'] = `Bearer ${authToken}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.Message || errorMessage;
        } catch {
          // If response is not JSON, use the default error message
        }
        
        throw new Error(errorMessage);
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return {} as T;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  // Get user notification preferences
  async getPreferences(): Promise<NotificationPreference[]> {
    return await this.makeRequest<NotificationPreference[]>('/notification/preferences');
  }

  // Update a specific notification preference
  async updatePreference(
    type: NotificationType, 
    request: UpdateNotificationPreferenceRequest
  ): Promise<NotificationPreference> {
    return await this.makeRequest<NotificationPreference>(
      `/notification/preferences/${type}`,
      {
        method: 'PUT',
        body: JSON.stringify(request),
      }
    );
  }

  // Get notification history
  async getHistory(limit: number = 50): Promise<NotificationHistoryItem[]> {
    return await this.makeRequest<NotificationHistoryItem[]>(
      `/notification/history?limit=${limit}`
    );
  }

  // Send test notification
  async sendTestNotification(type: NotificationType): Promise<void> {
    await this.makeRequest<void>(
      `/notification/test?type=${type}`,
      {
        method: 'POST',
      }
    );
  }

  // Request browser push permission and subscribe
  async requestBrowserPushPermission(): Promise<boolean> {
    if (!isNotificationSupported()) {
      console.warn('This browser does not support notifications');
      return false;
    }

    const currentPermission = getNotificationPermission();
    
    if (currentPermission === 'granted') {
      return true;
    }

    if (currentPermission === 'denied') {
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.warn('Failed to request notification permission:', error);
      return false;
    }
  }

  // Check if browser push is supported and enabled
  isBrowserPushSupported(): boolean {
    return isNotificationSupported() && 'serviceWorker' in navigator;
  }

  // Get current browser push permission status
  getBrowserPushPermission(): NotificationPermission {
    return getNotificationPermission();
  }
}

export const notificationApi = new NotificationApiService();
export default notificationApi; 